import React, { useState } from 'react';
import { View, Text, TouchableOpacity, ActivityIndicator, useColorScheme } from 'react-native';
import { Image } from 'expo-image';
import { Ionicons } from '@expo/vector-icons';
import { Gesture, GestureDetector } from 'react-native-gesture-handler';
import Animated, {
  useSharedValue,
  useAnimatedStyle,
  withSpring,
  interpolate,
  runOnJS,
} from 'react-native-reanimated';
import { Message } from '../types';
import { formatMessageTime, getFileTypeIcon, formatFileSize } from '../utils/chatHelpers';
import { downloadFile } from '../utils/fileUpload';
import { PollMessage } from '@/modules/polls';
import { AvatarPreviewModal } from '@/components/AvatarPreviewModal';

/** Swipe threshold in px to trigger reply */
const SWIPE_THRESHOLD = 60;

interface MessageBubbleProps {
  message: Message;
  isOwnMessage: boolean;
  showAvatar: boolean;
  currentUserId?: string;
  onLongPress?: (message: Message) => void;
  onAvatarPress?: (userId: string) => void;
  onFilePress?: (url: string, type: string) => void;
  onReply?: (message: Message) => void;
}

export const MessageBubble: React.FC<MessageBubbleProps> = ({
  message,
  isOwnMessage,
  showAvatar,
  currentUserId,
  onLongPress,
  onAvatarPress,
  onFilePress,
  onReply,
}) => {
  const [downloading, setDownloading] = useState(false);
  const [showAvatarPreview, setShowAvatarPreview] = useState(false);
  const colorScheme = useColorScheme();
  const isDark = colorScheme === 'dark';

  // Swipe-to-reply animation
  const translateX = useSharedValue(0);
  const hasTriggered = useSharedValue(false);

  const triggerReply = () => {
    onReply?.(message);
  };

  const panGesture = Gesture.Pan()
    .activeOffsetX(20)
    .failOffsetY([-10, 10])
    .onUpdate((event) => {
      // Only allow swiping right
      const clampedX = Math.max(0, Math.min(event.translationX, SWIPE_THRESHOLD + 20));
      translateX.value = clampedX;

      if (clampedX >= SWIPE_THRESHOLD && !hasTriggered.value) {
        hasTriggered.value = true;
        runOnJS(triggerReply)();
      }
    })
    .onEnd(() => {
      translateX.value = withSpring(0, { damping: 20, stiffness: 200 });
      hasTriggered.value = false;
    });

  const animatedRowStyle = useAnimatedStyle(() => ({
    transform: [{ translateX: translateX.value }],
  }));

  const replyIconStyle = useAnimatedStyle(() => ({
    opacity: interpolate(translateX.value, [0, SWIPE_THRESHOLD * 0.5, SWIPE_THRESHOLD], [0, 0.5, 1]),
    transform: [
      { scale: interpolate(translateX.value, [0, SWIPE_THRESHOLD], [0.5, 1], 'clamp') },
    ],
  }));

  /**
   * Handle avatar tap - show preview modal
   */
  const handleAvatarTap = () => {
    if (showAvatar && message.sender?.avatar_url) {
      setShowAvatarPreview(true);
    }
  };

  /**
   * Handle view profile from modal
   */
  const handleViewProfile = (userId: string) => {
    onAvatarPress?.(userId);
  };

  const handleDownload = async () => {
    if (!message.file_url || downloading) return;

    setDownloading(true);
    try {
      const fileName = message.file_name || `file_${Date.now()}`;
      const fileType = message.file_type || 'application/octet-stream';
      await downloadFile(message.file_url, fileName, fileType);
    } finally {
      setDownloading(false);
    }
  };

  /** Render the replied-to message preview (works for all message types) */
  const renderReplyTo = () => {
    if (!message.reply_to) return null;

    const replyContent = message.reply_to.type === 'image'
      ? '📷 Foto'
      : message.reply_to.type === 'file'
        ? '📎 Bestand'
        : message.reply_to.type === 'poll'
          ? '📊 Poll'
          : message.reply_to.content || 'Bericht';

    return (
      <View className={`mb-2 p-2 rounded-lg border-l-2 ${
        isOwnMessage
          ? 'bg-purple-700 border-purple-400'
          : 'bg-gray-100 dark:bg-gray-700 border-gray-400'
      }`}>
        <Text className={`text-xs font-medium mb-0.5 ${
          isOwnMessage ? 'text-purple-200' : 'text-gray-600 dark:text-gray-400'
        }`}>
          {message.reply_to.sender?.nickname || message.reply_to.sender?.full_name || 'Onbekend'}
        </Text>
        <Text
          className={`text-xs ${isOwnMessage ? 'text-purple-100' : 'text-gray-700 dark:text-gray-300'}`}
          numberOfLines={2}
        >
          {replyContent}
        </Text>
      </View>
    );
  };

  const renderContent = () => {
    if (message.is_deleted) {
      return (
        <Text className="text-gray-500 dark:text-gray-400 italic text-sm">
          Bericht verwijderd
        </Text>
      );
    }

    switch (message.type) {
      case 'image':
        return (
          <View>
            {renderReplyTo()}
            <TouchableOpacity
              onPress={() => onFilePress?.(message.file_url!, message.type)}
              activeOpacity={0.9}
            >
              <Image
                source={{ uri: message.file_url }}
                style={{ width: 200, height: 200, borderRadius: 12 }}
                contentFit="cover"
              />
            </TouchableOpacity>

            {/* Download button for images */}
            <TouchableOpacity
              onPress={handleDownload}
              disabled={downloading}
              className="absolute top-2 right-2 w-8 h-8 rounded-full items-center justify-center bg-black/40"
              activeOpacity={0.7}
              accessibilityLabel="Download afbeelding"
              accessibilityRole="button"
            >
              {downloading ? (
                <ActivityIndicator size="small" color="#FFFFFF" />
              ) : (
                <Ionicons name="download-outline" size={18} color="#FFFFFF" />
              )}
            </TouchableOpacity>

            {message.content && (
              <Text className={`mt-2 text-sm ${isOwnMessage ? 'text-white' : 'text-gray-900 dark:text-white'}`}>
                {message.content}
              </Text>
            )}
          </View>
        );

      case 'video':
      case 'file':
      case 'audio':
        return (
          <View>
            {renderReplyTo()}
            <TouchableOpacity
              onPress={() => onFilePress?.(message.file_url!, message.type)}
              className="flex-row items-center"
              activeOpacity={0.7}
            >
              <View className={`w-12 h-12 rounded-full items-center justify-center ${
                isOwnMessage ? 'bg-purple-700' : 'bg-gray-200 dark:bg-gray-700'
              }`}>
                <Text className="text-2xl">{getFileTypeIcon(message.file_type)}</Text>
              </View>
              <View className="ml-3 flex-1">
                <Text
                  className={`font-medium text-sm ${isOwnMessage ? 'text-white' : 'text-gray-900 dark:text-white'}`}
                  numberOfLines={1}
                >
                  {message.file_name || 'File'}
                </Text>
                <Text className={`text-xs ${isOwnMessage ? 'text-purple-200' : 'text-gray-500 dark:text-gray-400'}`}>
                  {formatFileSize(message.file_size)}
                </Text>
              </View>
              <Ionicons
                name="download-outline"
                size={20}
                color={isOwnMessage ? '#fff' : '#7C3AED'}
              />
            </TouchableOpacity>
          </View>
        );

      case 'poll':
        const pollId = message.metadata?.poll_id;
        if (pollId && currentUserId) {
          return (
            <View>
              {renderReplyTo()}
              <PollMessage
                pollId={pollId}
                currentUserId={currentUserId}
                isOwnMessage={isOwnMessage}
              />
            </View>
          );
        }
        return (
          <View>
            {renderReplyTo()}
            <View className="flex-row items-center">
              <Ionicons
                name="bar-chart-outline"
                size={20}
                color={isOwnMessage ? '#fff' : '#7C3AED'}
              />
              <Text className={`ml-2 text-sm ${isOwnMessage ? 'text-white' : 'text-gray-900 dark:text-white'}`}>
                {message.content || 'Poll'}
              </Text>
            </View>
          </View>
        );

      case 'text':
      default:
        return (
          <>
            {renderReplyTo()}
            <Text className={`text-sm ${isOwnMessage ? 'text-white' : 'text-gray-900 dark:text-white'}`}>
              {message.content}
            </Text>
          </>
        );
    }
  };

  const renderReactions = () => {
    if (!message.reactions || message.reactions.length === 0) return null;

    const groupedReactions: { [emoji: string]: number } = {};
    message.reactions.forEach((reaction) => {
      groupedReactions[reaction.emoji] = (groupedReactions[reaction.emoji] || 0) + 1;
    });

    return (
      <View className="flex-row flex-wrap gap-1 mt-1">
        {Object.entries(groupedReactions).map(([emoji, count]) => (
          <View
            key={emoji}
            className="flex-row items-center bg-gray-100 dark:bg-gray-700 rounded-full px-2 py-0.5"
          >
            <Text className="text-xs">{emoji}</Text>
            {count > 1 && (
              <Text className="text-xs ml-1 text-gray-600 dark:text-gray-400">
                {count}
              </Text>
            )}
          </View>
        ))}
      </View>
    );
  };

  return (
    <View className="relative">
      {/* Reply icon (behind the message, revealed on swipe) */}
      <Animated.View
        style={[
          {
            position: 'absolute',
            left: 4,
            top: 0,
            bottom: 0,
            justifyContent: 'center',
            alignItems: 'center',
            width: 36,
            height: '100%',
          },
          replyIconStyle,
        ]}
        pointerEvents="none"
      >
        <View
          style={{
            width: 32,
            height: 32,
            borderRadius: 16,
            backgroundColor: isDark ? '#374151' : '#E5E7EB',
            justifyContent: 'center',
            alignItems: 'center',
          }}
        >
          <Ionicons name="arrow-undo" size={18} color={isDark ? '#D1D5DB' : '#6B7280'} />
        </View>
      </Animated.View>

      {/* Swipeable message row */}
      <GestureDetector gesture={panGesture}>
        <Animated.View
          style={[
            { flexDirection: 'row', marginBottom: 12, justifyContent: isOwnMessage ? 'flex-end' : 'flex-start' },
            animatedRowStyle,
          ]}
        >
          {/* Avatar (for other users) */}
          {!isOwnMessage && (
            <TouchableOpacity
              onPress={handleAvatarTap}
              className="mr-2"
              disabled={!showAvatar}
              accessibilityLabel={showAvatar ? `Bekijk foto van ${message.sender?.full_name || 'gebruiker'}` : undefined}
              accessibilityRole="button"
            >
              {showAvatar ? (
                message.sender?.avatar_url ? (
                  <Image
                    source={{ uri: message.sender.avatar_url }}
                    style={{ width: 32, height: 32, borderRadius: 16 }}
                    contentFit="cover"
                  />
                ) : (
                  <View className="w-8 h-8 rounded-full bg-purple-100 dark:bg-purple-900 items-center justify-center">
                    <Ionicons name="person" size={16} color="#7C3AED" />
                  </View>
                )
              ) : (
                <View style={{ width: 32 }} />
              )}
            </TouchableOpacity>
          )}

          {/* Avatar Preview Modal */}
          <AvatarPreviewModal
            visible={showAvatarPreview}
            avatarUrl={message.sender?.avatar_url}
            userName={message.sender?.nickname || message.sender?.full_name}
            userId={message.sender_id}
            onClose={() => setShowAvatarPreview(false)}
            onViewProfile={handleViewProfile}
          />

          {/* Message bubble */}
          <TouchableOpacity
            onLongPress={() => onLongPress?.(message)}
            activeOpacity={0.9}
            className={`max-w-[75%] rounded-2xl p-3 ${
              isOwnMessage
                ? 'bg-purple-600 dark:bg-purple-700 rounded-tr-sm'
                : 'bg-gray-200 dark:bg-gray-700 rounded-tl-sm'
            }`}
          >
            {/* Sender name (for group chats, non-own messages) */}
            {!isOwnMessage && showAvatar && (
              <Text className="text-xs font-semibold mb-1 text-gray-700 dark:text-gray-300">
                {message.sender?.nickname || message.sender?.full_name || 'Unknown'}
              </Text>
            )}

            {/* Content */}
            {renderContent()}

            {/* Timestamp and edited badge */}
            <View className="flex-row items-center justify-end mt-1 gap-1">
              {message.is_edited && (
                <Text className={`text-xs ${isOwnMessage ? 'text-purple-200' : 'text-gray-500 dark:text-gray-400'}`}>
                  edited
                </Text>
              )}
              <Text className={`text-xs ${isOwnMessage ? 'text-purple-200' : 'text-gray-500 dark:text-gray-400'}`}>
                {formatMessageTime(message.created_at)}
              </Text>
            </View>

            {/* Reactions */}
            {renderReactions()}
          </TouchableOpacity>
        </Animated.View>
      </GestureDetector>
    </View>
  );
};
